// src/ctxReflector/components/Instances/views/Licensed/HealthCheckPage.tsx
import React, { useState, useEffect, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { GenericTable } from "@ui-components/Table/GenericTable";
import { fetchHealthCheck } from "./request/request";
import { HealthCheckItem, columns } from "./HealthCheckData";
import { useAppSelector } from "@redux/hooks";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar, {
  ParsedFilter,
} from "@ui-components/Searchbar/GenericSearchbar";
import { makeHealthCheckFields } from "./healthCheckFieldSpec";
import {
  buildPredicate,
  FilterField,
  labelId,
  parseToken,
} from "kontrolEdge/helpers/advancedFilters";
import { SkeletonTableRows } from "@ui-components/Skeleton/GenericSkeleton";

function HealthCheckPage() {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const { tenantId } = useAppSelector((state) => state.selection);
  const [items, setItems] = useState<HealthCheckItem[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  // Filtro local con GenericSearchbar
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const healthCheckFields = useMemo(() => makeHealthCheckFields(), []);

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    loadData();
  }, [isLoading, isAuthenticated, tenantId]);

  const loadData = async () => {
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      const resp = await fetchHealthCheck(token, tenantId);
      setItems(resp.data.data || []);
    } finally {
      setIsFetching(false);
    }
  };

  // Generate suggested keys from field labels
  const suggestedKeys = useMemo(() => {
    return healthCheckFields.map((f) => f.label);
  }, [healthCheckFields]);

  // Build maps for filter normalization
  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<HealthCheckItem>>();
    for (const f of healthCheckFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [healthCheckFields]);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of healthCheckFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [healthCheckFields]);

  // Normalize filters
  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      // If searchbar provided an op (live typing), keep it
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      // Map key to canonical id
      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      // If this is a STRING field and user didn't explicitly ask for exact,
      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1]; // strip quotes
          token.op = "=";
        } else {
          token.op = ":"; // default to contains
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  // Build predicate using advancedFilters system
  const predicate = useMemo(
    () => buildPredicate<HealthCheckItem>(normalizedFilters, healthCheckFields),
    [normalizedFilters, healthCheckFields]
  );

  // Apply predicate to filter data
  const filteredData = useMemo(
    () => items.filter(predicate),
    [items, predicate]
  );

  return (
    <div className="relative p-4">
      <div className="pb-6">
        <GenericCard
          isCollapsible={false}
          isLoading={isLoading}
          bodyContent={
            <>
              <div className="flex items-center mb-4 gap-2">
                <div className="w-full max-w-full sm:max-w-[500px]">
                  <GenericSearchbar
                    placeholder="Find by attribute or value (e.g., Name: CTX-R, Status)"
                    suggestedKeys={suggestedKeys}
                    onFiltersChange={setActiveFilters}
                    liveFiltering={true}
                    debounceMs={200}
                  />
                </div>
              </div>
              {isFetching ? (
                  <div className="p-4">
                      <SkeletonTableRows
                          rows={5}
                          cols={5}
                          colWidths={["200px"]}
                          rowHeight={15}
                          rowGapClassName="space-y-3"
                      />
                  </div>
              ) : (
                <GenericTable
                  columns={columns}
                  data={filteredData}
                  initialRowsPerPage={10}
                  rowsPerPageOptions={[5, 10, 20]}
                  width="100%"
                  tableClassName="bg-semantic-bg-primary"
                />
              )}
            </>
          }
        />
      </div>
    </div>
  );
}

export default HealthCheckPage;
